import { Link, useSearchParams } from "react-router-dom";
import type { Article } from "@/lib/api";
import { formatArticlePrice, formatDate } from "@/lib/format";
import { CATEGORIES } from "@/lib/categories";
import { Badge } from "@/components/ui/Badge";
import { Avatar } from "@/components/ui/Avatar";
import { Card } from "@/components/ui/Card";

const categoryNameMap = Object.fromEntries(
  CATEGORIES.map((c) => [c.slug, c.name]),
);

export function ArticleCard({ article }: { article: Article }) {
  const [, setSearchParams] = useSearchParams();

  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow">
      <Link to={`/article/${article.id}`}>
        {article.coverImageUrl && (
          <img
            src={article.coverImageUrl}
            alt={article.title}
            className="w-full h-48 object-cover"
          />
        )}
        <div className="p-5">
          <div className="flex items-center gap-2 mb-3">
            {article.writer && (
              <Link
                to={`/writer/${article.writerId}`}
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-2 hover:opacity-80"
              >
                <Avatar
                  src={article.writer.avatarUrl}
                  name={article.writer.displayName}
                  size="sm"
                />
                <span className="text-sm font-body text-grey">
                  {article.writer.displayName}
                </span>
              </Link>
            )}
            {article.publishedAt && (
              <span className="text-xs font-body text-beige ml-auto">
                {formatDate(article.publishedAt)}
              </span>
            )}
          </div>

          <h3 className="text-lg font-display font-bold mb-1 line-clamp-2">
            {article.title}
          </h3>
          {article.subtitle && (
            <p className="text-sm text-grey font-body mb-3 line-clamp-2">
              {article.subtitle}
            </p>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="orange">
              {formatArticlePrice(article.priceUsdc, article.priceEurc)}
            </Badge>
            {article.category && categoryNameMap[article.category] && (
              <span className="text-xs font-body text-beige bg-beige/10 px-2 py-0.5 rounded">
                {categoryNameMap[article.category]}
              </span>
            )}
            {(article.topics || []).slice(0, 3).map((topic) => (
              <button
                key={topic}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSearchParams({ topic });
                }}
                className="text-xs font-body text-grey border border-beige/30 px-2 py-0.5 rounded-full hover:border-orange/50 hover:text-orange transition-colors"
              >
                {topic}
              </button>
            ))}
          </div>
        </div>
      </Link>
    </Card>
  );
}
